import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { isTyping } from '../../lib/keyboard';

interface TicketShortcutHandlers {
  onReply: () => void;
  onNote: () => void;
  onTime: () => void;
  onHelp: () => void;
}

/**
 * Die Tastenkuerzel der Ticketmaske -- F1-44, K-208.
 *
 * **Einzelne Buchstaben, und deshalb nur ausserhalb von Eingabefeldern.**
 * Wer "a" fuer "Antwort" belegt, hat es im Betreff eines Tickets belegt --
 * ``isTyping`` haelt das ab. Ebenso alles mit Zusatztaste: ``Strg+A`` ist
 * "alles markieren" und bleibt es.
 *
 * **Die Buchstaben stehen in den Sprachdateien.** "a" fuer Antwort und "z"
 * fuer Zeit sind deutsche Merkhilfen; auf Englisch heissen dieselben Dinge
 * "reply" und "time". Ein fest verdrahtetes "a" waere in der englischen
 * Fassung eine Taste ohne Zusammenhang -- und ein Kuerzel, das man sich nicht
 * merken kann, benutzt niemand.
 *
 * Welche Taste welche Handlung ausloest, steht in der Uebersicht, die "?"
 * oeffnet. Ein Kuerzel, das nirgends steht, ist keines.
 */
export function useTicketShortcuts(handlers: TicketShortcutHandlers): void {
  const { t } = useTranslation();

  // Die Behandlungen wechseln bei jedem Darstellungslauf die Identitaet. Ohne
  // diesen Umweg meldete sich die Seite bei jedem Lauf ab und neu an -- ein
  // Aufwand fuer nichts, und ein Tastendruck genau dazwischen ginge verloren.
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Mit Zusatztaste gehoert das Kuerzel dem Browser oder der
      // Befehlspalette, nicht dieser Seite.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      const key = event.key.toLowerCase();

      if (key === t('shortcuts.keyReply')) {
        event.preventDefault();
        latest.current.onReply();
      } else if (key === t('shortcuts.keyNote')) {
        event.preventDefault();
        latest.current.onNote();
      } else if (key === t('shortcuts.keyTime')) {
        event.preventDefault();
        latest.current.onTime();
      } else if (event.key === '?') {
        event.preventDefault();
        latest.current.onHelp();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [t]);
}
